package org.multiplatform.kotfolio.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("kotest")
@Composable
fun KoTestPage() {
    BlogLayout(
        PageProperties(
            "kotest",
            "Kotest is a multiplatform Kotlin testing framework featuring powerful assertions, property testing, and multiple expressive styles for any project."
        ),
        BlogProperties("Testing Framework", "Kotest")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                    # Run all tests
                    ./gradlew test

                    # Run specific test class
                    ./gradlew test --tests "UserServiceFunSpecTest"

                    # Run tests matching a pattern
                    ./gradlew test --tests "*Service*"

                    # Run with verbose output
                    ./gradlew test --info

                    # Run in parallel
                    ./gradlew test --parallel
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    private val repository = mock<WaterRepository>()
                     
                    @BeforeTest
                    fun setUp() {
                        Dispatchers.setMain(testDispatcher)
                    }

                    @AfterTest
                    fun tearDown() {
                        Dispatchers.resetMain()
                    } 
                    """.trimIndent()
            )

            SpanText(
                text = "Different Types Of Testing Styles",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "StringSpec",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This one is straightforward. You give each test a string name and then put your test logic in a little block.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class StringSpecLength : StringSpec({
                        "length of hello should be 5" {
                            "hello".length shouldBe 5
                        }
                    })
                    
                    fun factorial(number: Int): Int
                        = (1..number).fold(1) { acc, i -> acc * i}
                        
                    class FactorialTest : StringSpec({
                        "Factorial of 5 is 120" {
                            factorial(5) shouldBeExactly 120
                        }
                    })    
                    """.trimIndent()
            )

            SpanText(
                text = "FunSpec",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "FunSpec allows you to create tests by invoking a function called test with a string argument to describe the test, and then the test itself as a lambda. If in doubt, this is the style to use. If you want to organize your tests a bit and maybe group some together, FunSpec is handy. You use context to group things and test for the actual test cases.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class MyTests : FunSpec({
                        test("String length should return the length of the string") {
                            "sammy".length shouldBe 5
                            "".length shouldBe 0
                        }
                    })
                    """.trimIndent()
            )

            SpanText(
                text = "ShouldSpec",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ShouldSpec is similar to fun spec but uses the keyword should instead of test.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class ShouldSpecTest : ShouldSpec({
                        should("return the length of the string") {
                            "sammy".length shouldBe 5
                            "".length shouldBe 0
                        }
                    })
                    
                    class ShouldSpecTest : ShouldSpec({
                        context("String.length") {
                            should("return the length of the string") {
                                "sammy".length shouldBe 5
                                "".length shouldBe 0
                            }
                        }
                    })
                    """.trimIndent()
            )

            SpanText(
                text = "DescribeSpec",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DescribeSpec offers a style familiar to those from a Ruby or Javascript background, as this testing style uses describe / it keywords. Tests must be nested in one or more describe blocks. context can also be used as an alias for describe.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class DescribeSpecTest : DescribeSpec({
                      describe("score") {
                        it("start as zero") {
                          // test here
                        }
                        describe("with a strike") {
                          it("adds ten") {
                            // test here
                          }
                          it("carries strike to the next frame") {
                            // test here
                          }
                        }

                        context("big scorers") {
                          describe("for the opposite team") {
                            it("Should negate one score") {
                              // test here
                            }
                          }
                        }
                      }
                    })
                    """.trimIndent()
            )

            SpanText(
                text = "BehaviorSpec",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Popular with people who like to write tests in the BDD (Behavior Driven Development) style, BehaviorSpec allows you to use context, given, when, then. Sometimes you want your tests to describe the steps a user might take.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class BehaviorSpecTest : BehaviorSpec({
                        given("an account with $100") {
                            `when`("the user withdraws $40") {
                                then("balance should be $60") {
                                    val account = Account(100)
                                    account.withdraw(40)
                                    account.balance shouldBe 60
                                }
                            }
                        }
                    })
                    
                    class BehaviorSpecTest : BehaviorSpec({
                        given("an account with $100") {
                            `when`("the user withdraws $40") {
                                then("balance should be $60") {
                                    val account = Account(100)
                                    account.withdraw(40)
                                    account.balance shouldBe 60
                                }
                            }
                            and("the user deposits $20") {
                                then("balance should be $120") {
                                    val account = Account(100)
                                    account.deposit(20)
                                    account.balance shouldBe 120
                                }
                            }
                        }
                    })
                    """.trimIndent()
            )

            SpanText(
                text = "FreeSpec",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "FreeSpec allows you to nest arbitrary levels of depth using the keyword - (minus) for outer tests, and just the test name for the final test.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "WordSpec",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "WordSpec uses the keyword should and uses that to nest tests after a context string.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class WordSpecShouldTest : WordSpec({
                        "String.length" should {
                            "return the length of the string" {
                                "sammy".length shouldBe 5
                                "".length shouldBe 0
                            }
                        }
                    })
                    
                    class WordSpecWhenTest : WordSpec({
                        "Hello" When {
                            "asked for length" should {
                                "return 5" {
                                    "Hello".length shouldBe 5
                                }
                            }
                            "appended to Bob" should {
                                "return Hello Bob" {
                                    "Hello " + "Bob" shouldBe "Hello Bob"
                                }
                            }
                        }
                    })
                    """.trimIndent()
            )

            SpanText(
                text = "every",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "every use to normal function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "everySuspend",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use everySuspend to mock Kotlin suspend functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "shouldContain, shouldContainExactly",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        @Test
                        fun `given list when add item then list is not modified`() {
                            val list = mutableListOf("Cat", "Dog", "Mouse")

                            list += "Lion"

                            list shouldContainExactly listOf("Cat", "Dog", "Mouse")
                        }
                """.trimIndent()
            )

            SpanText(
                text = "shouldBeInstanceOf",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Verifies that this value is an instance of T, which include any subclasses, and lets you execute block with that value casted.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class UserServiceFunSpecTest : FunSpec({

                        // Simple test case
                        test("should create user with valid email") {
                            val service = UserService()
                            val user = service.createUser("kamal.lakhani56@gmail.com", "kamlesh")

                            user.id shouldNotBe null
                            user.email shouldBe "kamal.lakhani56@gmail.com""
                            user.name shouldBe "kamlesh"
                        }

                        // Test with context for grouping related tests
                        context("when validating emails") {
                            test("should accept valid email formats") {
                                val validator = EmailValidator()

                                validator.isValid("kamal.lakhani56@gmail.com") shouldBe true
                                validator.isValid("kamal.lakhani56@gmail.com") shouldBe true
                            }

                            test("should reject invalid email formats") {
                                val validator = EmailValidator()

                                validator.isValid("invalid") shouldBe false
                                validator.isValid("@domain.com") shouldBe false
                            }
                        }
                    })
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import io.kotest.core.spec.style.BehaviorSpec
                    import io.kotest.matchers.shouldBe
                    import io.kotest.matchers.collections.shouldHaveSize

                    class OrderServiceBehaviorSpecTest : BehaviorSpec({

                        given("a shopping cart with items") {
                            val cart = ShoppingCart()
                            cart.addItem(Item("Laptop", 999.99))
                            cart.addItem(Item("Mouse", 29.99))

                            `when`("checkout is initiated") {
                                val orderService = OrderService()
                                val order = orderService.checkout(cart)

                                then("order should be created with all items") {
                                    order.items shouldHaveSize 2
                                }

                                then("order total should include all items") {
                                    order.total shouldBe 1029.98
                                }

                                then("order status should be pending") {
                                    order.status shouldBe OrderStatus.PENDING
                                }
                            }
                        }

                        given("an empty shopping cart") {
                            val cart = ShoppingCart()

                            `when`("checkout is attempted") {
                                val orderService = OrderService()

                                then("should throw an exception") {
                                    val exception = shouldThrow<EmptyCartException> {
                                        orderService.checkout(cart)
                                    }
                                    exception.message shouldBe "Cannot checkout empty cart"
                                }
                            }
                        }
                    })
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class CollectionMatchersTest : FunSpec({

                        test("list matchers") {
                            val numbers = listOf(1, 2, 3, 4, 5)

                            numbers shouldHaveSize 5
                            numbers shouldContain 3
                            numbers shouldContainAll listOf(1, 3, 5)
                            numbers shouldContainExactly listOf(1, 2, 3, 4, 5)
                            numbers.shouldBeSorted()
                            numbers shouldHaveAtLeastSize 3
                        }

                        test("checking element presence") {
                            val fruits = listOf("apple", "banana", "cherry")

                            fruits shouldContainAnyOf listOf("apple", "grape")
                            fruits shouldNotContain "grape"
                            fruits.shouldContainInOrder("apple", "cherry")
                        }

                        test("map matchers") {
                            val config = mapOf(
                                "host" to "localhost",
                                "port" to 8080,
                                "debug" to true
                            )

                            config shouldHaveSize 3
                            config shouldContainKey "host"
                            config shouldContainValue 8080
                            config shouldContain ("debug" to true)
                            config.shouldNotBeEmpty()
                        }

                        test("empty and single element") {
                            val empty = emptyList<String>()
                            val single = listOf("only")

                            empty.shouldBeEmpty()
                            single.shouldHaveSingleElement("only")
                        }
                    })
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    
                    data class User(val id: Int, val name: String)

                    class UserRepository {
                        private val users = mutableMapOf(1 to User(1, "Alice"), 2 to User(2, "Bob"))
                        fun getUser(id: Int): User? = users[id]
                        fun addUser(user: User) { users[user.id] = user }
                        fun deleteUser(id: Int) { users.remove(id) }
                    }

                    class UserRepositoryTest : FunSpec({
                        val repository = UserRepository()

                        context("getting users") {
                            test("should return user by ID") {
                                val user = repository.getUser(1)
                                user shouldBe User(1, "Alice")
                                user?.name shouldContain "Ali"
                            }

                            test("should return null for invalid ID") {
                                repository.getUser(-1) shouldBe null
                            }
                        }

                        context("adding and deleting users") {
                            test("should add a new user") {
                                val newUser = User(3, "Charlie")
                                repository.addUser(newUser)
                                repository.getUser(3) shouldBe newUser
                            }

                            test("should delete a user") {
                                repository.deleteUser(2)
                                repository.getUser(2) shouldBe null
                            }
                        }
                    })
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    interface WaterRepository { 
                        suspend fun updateDailyGoal(goalMl: Int): Result<Unit>
                    }
                    
                    @Inject
                    class UpdateDailyGoalUseCase(
                        private val waterRepository: WaterRepository,
                    ) {
                        suspend operator fun invoke(goalMl: Int): Result<Unit> {
                            if (goalMl < MIN_GOAL || goalMl > MAX_GOAL) {
                                return Result.failure(IllegalArgumentException("Goal must be between $MIN_GOAL and $MAX_GOAL ml"))
                            }
                            return waterRepository.updateDailyGoal(goalMl)
                        }

                        companion object {
                            const val MIN_GOAL = 500
                            const val MAX_GOAL = 5000
                        }
                    }
                    
                    class UpdateDailyGoalUseCaseTest {
                        private val repository = mock<WaterRepository>()
                        private val useCase = UpdateDailyGoalUseCase(repository)

                        @Test
                        fun `invoke calls repository when goal is within range`() =
                            runTest {
                                val goalMl = 2500
                                val expectedResult = Result.success(Unit)

                                everySuspend { repository.updateDailyGoal(goalMl) } returns expectedResult

                                val result = useCase(goalMl)

                                result shouldBe expectedResult
                                verifySuspend { repository.updateDailyGoal(goalMl) }
                            }

                        @Test
                        fun `invoke returns failure when goal is below minimum`() =
                            runTest {
                                val goalMl = UpdateDailyGoalUseCase.MIN_GOAL - 1

                                val result = useCase(goalMl)

                                result.isFailure shouldBe true
                                result.exceptionOrNull().shouldBeInstanceOf<IllegalArgumentException>()
                                verifySuspend(mode = VerifyMode.not) { repository.updateDailyGoal(any()) }
                            }

                        @Test
                        fun `invoke returns failure when goal is above maximum`() =
                            runTest {
                                val goalMl = UpdateDailyGoalUseCase.MAX_GOAL + 1

                                val result = useCase(goalMl)

                                result.isFailure shouldBe true
                                result.exceptionOrNull().shouldBeInstanceOf<IllegalArgumentException>()
                                verifySuspend(mode = VerifyMode.not) { repository.updateDailyGoal(any()) }
                            }

                        @Test
                        fun `invoke calls repository when goal is exactly minimum`() =
                            runTest {
                                val goalMl = UpdateDailyGoalUseCase.MIN_GOAL
                                everySuspend { repository.updateDailyGoal(goalMl) } returns Result.success(Unit)

                                val result = useCase(goalMl)

                                result.isSuccess shouldBe true
                                verifySuspend { repository.updateDailyGoal(goalMl) }
                            }

                        @Test
                        fun `invoke calls repository when goal is exactly maximum`() =
                            runTest {
                                val goalMl = UpdateDailyGoalUseCase.MAX_GOAL
                                everySuspend { repository.updateDailyGoal(goalMl) } returns Result.success(Unit)

                                val result = useCase(goalMl)

                                result.isSuccess shouldBe true
                                verifySuspend { repository.updateDailyGoal(goalMl) }
                            }
                    }
                    """.trimIndent()
            )

            //https://www.kmpship.app/blog/kotlin-multiplatform-testing-guide-2025
            KotlinCode(
                code = """
                    //common
                    class PaymentValidator {
                        sealed class ValidationResult {
                            data object Valid : ValidationResult()
                            data class Invalid(val errors: List<ValidationError>) : ValidationResult()
                        }

                        enum class ValidationError {
                            AMOUNT_TOO_LOW,
                            AMOUNT_TOO_HIGH,
                            INVALID_CURRENCY,
                            CARD_EXPIRED,
                            INSUFFICIENT_FUNDS
                        }

                        fun validate(
                            amount: Double,
                            currency: String,
                            cardExpiryMonth: Int,
                            cardExpiryYear: Int,
                            availableBalance: Double
                        ): ValidationResult {
                            val errors = mutableListOf<ValidationError>()

                            // Amount validation
                            if (amount <= 0.0) errors.add(ValidationError.AMOUNT_TOO_LOW)
                            if (amount > 10000.0) errors.add(ValidationError.AMOUNT_TOO_HIGH)

                            // Currency validation
                            if (currency !in listOf("USD", "EUR", "GBP")) {
                                errors.add(ValidationError.INVALID_CURRENCY)
                            }

                            // Expiry validation
                            val currentYear = getCurrentYear()
                            val currentMonth = getCurrentMonth()
                            if (cardExpiryYear < currentYear ||
                                (cardExpiryYear == currentYear && cardExpiryMonth < currentMonth)) {
                                errors.add(ValidationError.CARD_EXPIRED)
                            }

                            // Balance validation
                            if (amount > availableBalance) {
                                errors.add(ValidationError.INSUFFICIENT_FUNDS)
                            }

                            return if (errors.isEmpty()) {
                                ValidationResult.Valid
                            } else {
                                ValidationResult.Invalid(errors)
                            }
                        }

                        // Platform-agnostic date helpers
                        private fun getCurrentYear(): Int = 2025  // In real code, use expect/actual
                        private fun getCurrentMonth(): Int = 1
                    }
                    
                    //kotest
                    import io.kotest.matchers.shouldBe
                    import io.kotest.matchers.types.shouldBeInstanceOf
                    import kotlin.test.Test

                    class PaymentValidatorTest {
                        private val validator = PaymentValidator()

                        @Test
                        fun `valid payment passes all checks`() {
                            val result = validator.validate(
                                amount = 50.0,
                                currency = "USD",
                                cardExpiryMonth = 12,
                                cardExpiryYear = 2026,
                                availableBalance = 100.0
                            )

                            result.shouldBeInstanceOf<PaymentValidator.ValidationResult.Valid>()
                        }

                        @Test
                        fun `zero amount is rejected`() {
                            val result = validator.validate(
                                amount = 0.0,
                                currency = "USD",
                                cardExpiryMonth = 12,
                                cardExpiryYear = 2026,
                                availableBalance = 100.0
                            )

                            result.shouldBeInstanceOf<PaymentValidator.ValidationResult.Invalid>()
                            val invalid = result as PaymentValidator.ValidationResult.Invalid
                            invalid.errors shouldBe listOf(PaymentValidator.ValidationError.AMOUNT_TOO_LOW)
                        }

                        @Test
                        fun `amount exceeding limit is rejected`() {
                            val result = validator.validate(
                                amount = 15000.0,
                                currency = "USD",
                                cardExpiryMonth = 12,
                                cardExpiryYear = 2026,
                                availableBalance = 20000.0
                            )

                            result.shouldBeInstanceOf<PaymentValidator.ValidationResult.Invalid>()
                            val invalid = result as PaymentValidator.ValidationResult.Invalid
                            invalid.errors shouldBe listOf(PaymentValidator.ValidationError.AMOUNT_TOO_HIGH)
                        }

                        @Test
                        fun `unsupported currency is rejected`() {
                            val result = validator.validate(
                                amount = 50.0,
                                currency = "JPY",
                                cardExpiryMonth = 12,
                                cardExpiryYear = 2026,
                                availableBalance = 100.0
                            )

                            result.shouldBeInstanceOf<PaymentValidator.ValidationResult.Invalid>()
                            val invalid = result as PaymentValidator.ValidationResult.Invalid
                            invalid.errors shouldBe listOf(PaymentValidator.ValidationError.INVALID_CURRENCY)
                        }

                        @Test
                        fun `expired card is rejected`() {
                            val result = validator.validate(
                                amount = 50.0,
                                currency = "USD",
                                cardExpiryMonth = 12,
                                cardExpiryYear = 2024,
                                availableBalance = 100.0
                            )

                            result.shouldBeInstanceOf<PaymentValidator.ValidationResult.Invalid>()
                            val invalid = result as PaymentValidator.ValidationResult.Invalid
                            invalid.errors shouldBe listOf(PaymentValidator.ValidationError.CARD_EXPIRED)
                        }

                        @Test
                        fun `insufficient balance is rejected`() {
                            val result = validator.validate(
                                amount = 150.0,
                                currency = "USD",
                                cardExpiryMonth = 12,
                                cardExpiryYear = 2026,
                                availableBalance = 100.0
                            )

                            result.shouldBeInstanceOf<PaymentValidator.ValidationResult.Invalid>()
                            val invalid = result as PaymentValidator.ValidationResult.Invalid
                            invalid.errors shouldBe listOf(PaymentValidator.ValidationError.INSUFFICIENT_FUNDS)
                        }

                        @Test
                        fun `multiple validation errors are accumulated`() {
                            val result = validator.validate(
                                amount = 15000.0,
                                currency = "JPY",
                                cardExpiryMonth = 12,
                                cardExpiryYear = 2024,
                                availableBalance = 100.0
                            )

                            result.shouldBeInstanceOf<PaymentValidator.ValidationResult.Invalid>()
                            val invalid = result as PaymentValidator.ValidationResult.Invalid

                            // Should catch all four errors
                            invalid.errors.size shouldBe 4
                            invalid.errors.toSet() shouldBe setOf(
                                PaymentValidator.ValidationError.AMOUNT_TOO_HIGH,
                                PaymentValidator.ValidationError.INVALID_CURRENCY,
                                PaymentValidator.ValidationError.CARD_EXPIRED,
                                PaymentValidator.ValidationError.INSUFFICIENT_FUNDS
                            )
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//Best Library TestCase: testBalloon
//Spy in MockK (Dynamic json based, calculation)
//kotest with Turbine for flows
//kotest-assertions-ktor

//https://oneuptime.com/blog/post/2026-02-02-kotlin-kotest-unit-tests/view
//https://www.kmpship.app/blog/kotlin-multiplatform-testing-guide-2025
//https://softwaremill.com/kotest-the-kotlin-testing-framework-you-will-love
//https://github.com/tezov/koin-education.git

//Kotzila

//detektPlugins
//https://blog.insert-koin.io/detekt-rules-koin-c0b6330fc37b